/*
 * Public API Surface of izzy-ui
 */

export * from './lib/izzy-ui.module';

// UI components
export * from './lib/components/message-approval/message-approval.component';
export * from './lib/components/messages-container/messages-container.component';
export * from './lib/components/settings/settings.component';
export * from './lib/components/organizations/organizations.component';
export * from './lib/components/channel-actions/channel-actions.component';
export * from './lib/components/api-keys/api-keys.component';

// Action Governance UI components (v2.0)
export * from './lib/components/action-gallery/action-gallery.component';
export * from './lib/components/organization-actions/organization-actions.component';
export * from './lib/components/channel-type-actions/channel-type-actions.component';

// Core entity management components (v2.0)
export * from './lib/components/contacts/contacts.component';
export * from './lib/components/contact-roles/contact-roles.component';
export * from './lib/components/organization-members/organization-members.component';
export * from './lib/components/plans/plans.component';
export * from './lib/components/channel-types/channel-types.component';
export * from './lib/components/channels/channels.component';
export * from './lib/components/channel-runs/channel-runs.component';
export * from './lib/components/scopes/scopes.component';
export * from './lib/components/api-key-scopes/api-key-scopes.component';
export * from './lib/components/private-action-grants/private-action-grants.component';

// Credentials management components (v2.1)
export * from './lib/components/credentials/credentials.component';
export * from './lib/components/credential-types/credential-types.component';

// AI Configuration management (v2.2)
export * from './lib/components/ai-configuration/ai-configuration.component';

// Shared UI components
export * from './lib/components/org-selector/org-selector.component';
export * from './lib/components/global-context/global-context.component';
export * from './lib/components/profile-completion/profile-completion.component';

// Message Editor with WYSIWYG and Track Changes (v2.1)
export * from './lib/components/message-editor/message-editor.component';
export * from './lib/components/writing-assist-menu/writing-assist-menu.component';
export * from './lib/services/izzy-writing-assist.service';

// Reusable Message Viewer component (v2.1)
export * from './lib/components/message-viewer/message-viewer.component';

// AI Analysis pane, shared by the Messages and Ticket screens (v4.1)
export * from './lib/components/ai-analysis/ai-analysis.component';

// Message Card component with state-aware rendering (v2.3)

// Payload Viewer for AI processing pipeline visualization (v2.3)
export * from './lib/components/payload-viewer/payload-viewer.component';

// Diff Viewer component
export * from './lib/components/diff-viewer/diff-viewer.component';

// Unified Administration Console
export * from './lib/components/administration/administration.component';

// Analytics Dashboard
export * from './lib/components/analytics/analytics.component';

// Setup Agent
export * from './lib/components/setup-agent/setup-agent.component';

// Ticket Workspace (top-level agent-facing Resource) + queue + detail (PRD v4.0 §11)
export * from './lib/components/tickets/ticket-workspace/ticket-workspace.component';
export * from './lib/components/tickets/ticket-queue/ticket-queue.component';
export * from './lib/components/tickets/ticket-detail/ticket-detail.component';
// Ticket Teams admin panel (Phase 2 — team management under Administration)
export * from './lib/components/ticket-teams/ticket-teams.component';
// SLA Policies admin panel (Phase 3 — SLA targets under Administration)
export * from './lib/components/sla-policies/sla-policies.component';
// Ticket Numbering admin panel (PRD §5.2 — per-org ticket number + prefix)
export * from './lib/components/ticket-numbering/ticket-numbering.component';

// Assignment Rules admin panel (§9.3 — ticket routing rules under Administration)
export * from './lib/components/assignment-rules/assignment-rules.component';

// Skills admin panel (§9.4 — skills-based routing under Administration)
export * from './lib/components/skills/skills.component';

// Automation Rules admin panel (§12 — event→condition→action under Administration)
export * from './lib/components/automation-rules/automation-rules.component';

// Macros admin panel (§11.6 — canned replies under Administration)
export * from './lib/components/macros/macros.component';

// Ticketing admin panel (consolidates Teams/SLA/Assignment/Skills/Automation/Macros into one tabbed section)
export * from './lib/components/ticketing-admin/ticketing-admin.component';

// Application
export * from './lib/izzy-application';

// Services
export * from './lib/services/izzy-auth.service';
export * from './lib/services/izzy-context.service';
export * from './lib/services/izzy-presence.service';

// Loader function to prevent tree-shaking
import { LoadMessageApprovalComponent } from './lib/components/message-approval/message-approval.component';
import { LoadSettingsComponent } from './lib/components/settings/settings.component';
import { LoadOrganizationsComponent } from './lib/components/organizations/organizations.component';
import { LoadChannelActionsComponent } from './lib/components/channel-actions/channel-actions.component';
import { LoadAPIKeysComponent } from './lib/components/api-keys/api-keys.component';
import { LoadActionGalleryComponent } from './lib/components/action-gallery/action-gallery.component';
import { LoadOrganizationActionsComponent } from './lib/components/organization-actions/organization-actions.component';
import { LoadChannelTypeActionsComponent } from './lib/components/channel-type-actions/channel-type-actions.component';
import { LoadContactsComponent } from './lib/components/contacts/contacts.component';
import { LoadContactRolesComponent } from './lib/components/contact-roles/contact-roles.component';
import { LoadOrganizationMembersComponent } from './lib/components/organization-members/organization-members.component';
import { LoadPlansComponent } from './lib/components/plans/plans.component';
import { LoadChannelTypesComponent } from './lib/components/channel-types/channel-types.component';
import { LoadChannelsComponent } from './lib/components/channels/channels.component';
import { LoadChannelRunsComponent } from './lib/components/channel-runs/channel-runs.component';
import { LoadScopesComponent } from './lib/components/scopes/scopes.component';
import { LoadAPIKeyScopesComponent } from './lib/components/api-key-scopes/api-key-scopes.component';
import { LoadPrivateActionGrantsComponent } from './lib/components/private-action-grants/private-action-grants.component';
import { LoadCredentialsComponent } from './lib/components/credentials/credentials.component';
import { LoadCredentialTypesComponent } from './lib/components/credential-types/credential-types.component';
import { LoadAIConfigurationComponent } from './lib/components/ai-configuration/ai-configuration.component';
import { LoadOrgSelectorComponent } from './lib/components/org-selector/org-selector.component';
import { LoadProfileCompletionComponent } from './lib/components/profile-completion/profile-completion.component';
import { LoadAdministrationComponent } from './lib/components/administration/administration.component';
import { LoadAnalyticsComponent } from './lib/components/analytics/analytics.component';
import { LoadMessagesContainerComponent } from './lib/components/messages-container/messages-container.component';
import { LoadMessageViewerComponent } from './lib/components/message-viewer/message-viewer.component';
import { LoadPayloadViewerComponent } from './lib/components/payload-viewer/payload-viewer.component';
import { LoadTicketWorkspaceComponent } from './lib/components/tickets/ticket-workspace/ticket-workspace.component';
import { LoadTicketQueueComponent } from './lib/components/tickets/ticket-queue/ticket-queue.component';
import { LoadTicketDetailComponent } from './lib/components/tickets/ticket-detail/ticket-detail.component';
import { LoadTicketTeamsComponent } from './lib/components/ticket-teams/ticket-teams.component';
import { LoadSLAPoliciesComponent } from './lib/components/sla-policies/sla-policies.component';
import { LoadTicketNumberingComponent } from './lib/components/ticket-numbering/ticket-numbering.component';
import { LoadAssignmentRulesComponent } from './lib/components/assignment-rules/assignment-rules.component';
import { LoadSkillsComponent } from './lib/components/skills/skills.component';
import { LoadAutomationRulesComponent } from './lib/components/automation-rules/automation-rules.component';
import { LoadMacrosComponent } from './lib/components/macros/macros.component';
import { LoadIzzyApplication } from './lib/izzy-application';

export function LoadIzzyUI(): void {
  // Application class
  LoadIzzyApplication();
  // UI components
  LoadMessageApprovalComponent();
  LoadSettingsComponent();
  LoadOrganizationsComponent();
  LoadChannelActionsComponent();
  LoadAPIKeysComponent();
  // Action Governance (v2.0)
  LoadActionGalleryComponent();
  LoadOrganizationActionsComponent();
  LoadChannelTypeActionsComponent();
  // Core entity management (v2.0)
  LoadContactsComponent();
  LoadContactRolesComponent();
  LoadOrganizationMembersComponent();
  LoadPlansComponent();
  LoadChannelTypesComponent();
  LoadChannelsComponent();
  LoadChannelRunsComponent();
  LoadScopesComponent();
  LoadAPIKeyScopesComponent();
  LoadPrivateActionGrantsComponent();
  // Credentials management (v2.1)
  LoadCredentialsComponent();
  LoadCredentialTypesComponent();
  // AI Configuration (v2.2)
  LoadAIConfigurationComponent();
  // Shared UI components
  LoadOrgSelectorComponent();
  LoadProfileCompletionComponent();
  // Unified Administration Console
  LoadAdministrationComponent();
  // Analytics Dashboard
  LoadAnalyticsComponent();
  // Messages Container Dashboard
  LoadMessagesContainerComponent();
  // Message Viewer (v2.1)
  LoadMessageViewerComponent();
  // Message Card (v2.3)
  // Payload Viewer (v2.3)
  LoadPayloadViewerComponent();
  // Ticket Workspace + queue + detail (PRD v4.0 §11)
  LoadTicketWorkspaceComponent();
  LoadTicketQueueComponent();
  LoadTicketDetailComponent();
  LoadTicketTeamsComponent();
  LoadSLAPoliciesComponent();
  LoadTicketNumberingComponent();
  LoadAssignmentRulesComponent();
  LoadSkillsComponent();
  LoadAutomationRulesComponent();
  LoadMacrosComponent();
}
